import React from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { EventContentTabProps } from "./ComponentPropsV3";

const EventContentTabV3: React.FC<EventContentTabProps> = ({
  agendaSlots,
  setAgendaSlots,
  setShowAgendaModal,
  setEditingAgenda,
  lineupMembers,
  setLineupMembers,
  setShowLineupModal,
  setEditingLineup,
  sponsorsList,
  setSponsorsList,
  setShowSponsorModal,
  setEditingSponsor,
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Agenda */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="v3-section-head">Event Agenda / Timelines</div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: "4px 0 0 0" }}>
              Define core blocks of schedules for your attendees to digest.
            </p>
          </div>
          <button
            type="button"
            className="v3-btn-primary"
            style={{ fontSize: 12, padding: "8px 16px" }}
            onClick={() => {
              setEditingAgenda(null);
              setShowAgendaModal(true);
            }}
          >
            <Plus size={14} /> Add Agenda Item
          </button>
        </div>

        {agendaSlots.length === 0 ? (
          <div style={{ padding: "24px 16px", textAlign: "center", borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
            No agenda timeline slots added yet. Click "+ Add Agenda Item" to create one.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {agendaSlots.map((slot) => (
              <div
                key={slot.id || slot.title}
                style={{
                  padding: 14,
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  {slot.hostImageUrl ? (
                    <img src={slot.hostImageUrl} alt={slot.hostName || "Host"} style={{ width: 44, height: 44, borderRadius: 10, objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: "rgba(238,63,124,0.15)", border: "1px solid rgba(238,63,124,0.3)", display: "flex", alignItems: "center", justifyContent: "center", color: "#EE3F7C", fontSize: 11, fontWeight: 700 }}>
                      {slot.startTime ? slot.startTime.slice(0, 5) : "SESSION"}
                    </div>
                  )}
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10, fontFamily: "monospace", color: "#F79B59", fontWeight: 700, background: "rgba(247,155,89,0.15)", padding: "2px 6px", borderRadius: 4 }}>
                        {slot.startTime} {slot.endTime ? `- ${slot.endTime}` : ""}
                      </span>
                      {slot.hostName && (
                        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>
                          By {slot.hostName}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginTop: 4 }}>{slot.title}</div>
                    {slot.description && (
                      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: "2px 0 0 0" }}>{slot.description}</p>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingAgenda(slot);
                      setShowAgendaModal(true);
                    }}
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", borderRadius: 8, padding: 6, cursor: "pointer" }}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setAgendaSlots(agendaSlots.filter((s) => s.id !== slot.id))}
                    style={{ background: "rgba(255,93,115,0.15)", border: "1px solid rgba(255,93,115,0.3)", color: "#FF5D73", borderRadius: 8, padding: 6, cursor: "pointer" }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lineup */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="v3-section-head">Artist &amp; Speaker Lineup</div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: "4px 0 0 0" }}>
              Showcase featured performers, keynote speakers, and hosts.
            </p>
          </div>
          <button
            type="button"
            className="v3-btn-primary"
            style={{ fontSize: 12, padding: "8px 16px" }}
            onClick={() => {
              setEditingLineup(null);
              setShowLineupModal(true);
            }}
          >
            <Plus size={14} /> Add Performer / Speaker
          </button>
        </div>

        {lineupMembers.length === 0 ? (
          <div style={{ padding: "24px 16px", textAlign: "center", borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
            No performers or speakers added yet. Click "+ Add Performer / Speaker" to create one.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {lineupMembers.map((m) => (
              <div
                key={m.id || m.name}
                style={{
                  padding: 14,
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {m.artistImageUrl ? (
                    <img src={m.artistImageUrl} alt={m.name} style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg, #EE3F7C 0%, #F79B59 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700 }}>
                      {m.name ? m.name.slice(0, 2).toUpperCase() : "ART"}
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{m.name}</div>
                    {m.tagLine && <div style={{ fontSize: 10, color: "#EE3F7C", fontWeight: 600 }}>{m.tagLine}</div>}
                    {m.description && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>{m.description}</div>}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingLineup(m);
                      setShowLineupModal(true);
                    }}
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", borderRadius: 8, padding: 6, cursor: "pointer" }}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setLineupMembers(lineupMembers.filter((item) => item.id !== m.id))}
                    style={{ background: "rgba(255,93,115,0.15)", border: "1px solid rgba(255,93,115,0.3)", color: "#FF5D73", borderRadius: 8, padding: 6, cursor: "pointer" }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sponsors */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="v3-section-head">Brand Sponsors</div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: "4px 0 0 0" }}>
              Feature corporate sponsors and brand partners with official logos and links.
            </p>
          </div>
          <button
            type="button"
            className="v3-btn-primary"
            style={{ fontSize: 12, padding: "8px 16px" }}
            onClick={() => {
              setEditingSponsor(null);
              setShowSponsorModal(true);
            }}
          >
            <Plus size={14} /> Add Brand Sponsor
          </button>
        </div>

        {sponsorsList.length === 0 ? (
          <div style={{ padding: "24px 16px", textAlign: "center", borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
            No brand sponsors added yet. Click "+ Add Brand Sponsor" to create one.
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {sponsorsList.map((s) => (
              <div
                key={s.id || s.name}
                style={{
                  padding: "10px 14px",
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                {s.thumbImageUrl && (
                  <img src={s.thumbImageUrl} alt={s.name} style={{ width: 32, height: 32, borderRadius: 6, objectFit: "contain", background: "rgba(255,255,255,0.05)" }} />
                )}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{s.name}</div>
                  <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: "rgba(238,63,124,0.2)", color: "#EE3F7C", textTransform: "uppercase", fontWeight: 700, display: "inline-block", marginTop: 2 }}>
                    {s.sponsorType?.title || "Sponsor"}
                  </span>
                </div>

                <div style={{ display: "flex", gap: 4, marginLeft: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSponsor(s);
                      setShowSponsorModal(true);
                    }}
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", borderRadius: 6, padding: 4, cursor: "pointer" }}
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setSponsorsList(sponsorsList.filter((sp) => sp.id !== s.id))}
                    style={{ background: "rgba(255,93,115,0.15)", border: "1px solid rgba(255,93,115,0.3)", color: "#FF5D73", borderRadius: 6, padding: 4, cursor: "pointer" }}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventContentTabV3;
